import { useCallback, useEffect, useState } from "react";
import { Eyebrow } from "./components/Eyebrow";
import { FALLBACK_INDUSTRIES, FALLBACK_ITEMS } from "./lib/fallbackMasters";
import type { Industry, Item } from "./lib/types";
import { AdminSecrets } from "./views/AdminSecrets";
import { MasterAdmin } from "./views/MasterAdmin";
import { ProposalBuilder } from "./views/ProposalBuilder";
import { ProposalList } from "./views/ProposalList";

type Tab = "build" | "history" | "master" | "settings";

async function check(r: Response) {
  if (r.status === 401) throw new Error("AUTH_EXPIRED");
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

const TABS: { id: Tab; label: string; meta: string }[] = [
  { id: "build", label: "提案を作る", meta: "01" },
  { id: "history", label: "過去の提案", meta: "02" },
  { id: "master", label: "マスタ管理", meta: "03" },
  { id: "settings", label: "設定", meta: "04" },
];

export function App() {
  const [tab, setTab] = useState<Tab>("build");
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [bootError, setBootError] = useState<string | null>(null);
  // 商談モード: 客先で画面を見せる時に原価・粗利を隠す。localStorageで永続化
  const [negotiationMode, setNegotiationMode] = useState<boolean>(
    () => localStorage.getItem("negotiation_mode") === "1",
  );
  const toggleNegotiation = () => {
    setNegotiationMode((v) => {
      const next = !v;
      localStorage.setItem("negotiation_mode", next ? "1" : "0");
      return next;
    });
  };

  const loadMasters = useCallback(() => {
    Promise.all([
      fetch("/api/industries").then(check),
      fetch("/api/items").then(check),
    ])
      .then(([i, it]: [{ industries: Industry[] }, { items: Item[] }]) => {
        setIndustries(i.industries);
        setItems(it.items);
        setBootError(null);
      })
      .catch((e: unknown) => {
        if (import.meta.env.DEV) {
          setIndustries(FALLBACK_INDUSTRIES);
          setItems(FALLBACK_ITEMS);
          setBootError(null);
          return;
        }
        setBootError(e instanceof Error ? e.message : String(e));
      });
  }, []);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-ink/10 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <Eyebrow>Mind Sales Intelligence Hub</Eyebrow>
            <h1 className="text-lg font-semibold tracking-tight mt-1">営業支援ダッシュボード</h1>
          </div>
          <button
            type="button"
            onClick={toggleNegotiation}
            aria-pressed={negotiationMode}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 transition ${
              negotiationMode
                ? "bg-amber-500 text-white ring-amber-500"
                : "bg-white text-ink-muted ring-ink/15 hover:ring-ink/40"
            }`}
          >
            {negotiationMode ? "● 商談モード中" : "商談モード"}
          </button>
        </div>
        <nav className="max-w-5xl mx-auto px-6 flex gap-6 text-sm">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`relative py-3 -mb-px transition ${
                  active ? "text-ink" : "text-ink-muted hover:text-ink"
                }`}
              >
                <span className="font-mono text-[10px] mr-2 text-ink-muted">{t.meta}</span>
                {t.label}
                {active && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-ink" />}
              </button>
            );
          })}
          {/* 独立ツール（別ページ配信）へのリンク。タブstateとは別導線 */}
          <a
            href="/tools/overseas-quote/"
            className="relative py-3 -mb-px transition text-ink-muted hover:text-ink ml-auto"
          >
            <span className="font-mono text-[10px] mr-2 text-ink-muted">T1</span>
            海外工場見積もり
            <span className="ml-1 text-[10px] text-ink-muted">↗</span>
          </a>
        </nav>
      </header>

      {negotiationMode && (
        <div className="bg-amber-500 text-white text-center text-xs py-1.5 font-medium">
          商談モード中：原価・粗利・値引き下限・競合レンジは非表示になっています
        </div>
      )}

      <main className="flex-1 bg-ink/[0.015]">
        <div className="max-w-5xl mx-auto px-6 py-10">
          {bootError && (
            <div className="mb-6 px-4 py-3 ring-1 ring-red-300 bg-red-50 text-sm text-red-800 rounded">
              {bootError === "AUTH_EXPIRED"
                ? "認証情報が失効している可能性があります。ページを再読み込みして再ログインしてください。"
                : `データ取得エラー: ${bootError}`}
            </div>
          )}
          {tab === "build" && (
            <ProposalBuilder industries={industries} items={items} hideCosts={negotiationMode} />
          )}
          {tab === "history" && <ProposalList />}
          {tab === "master" && (
            <MasterAdmin industries={industries} items={items} onItemsChanged={loadMasters} />
          )}
          {tab === "settings" && <AdminSecrets />}
        </div>
      </main>

      <footer className="border-t border-ink/10 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 text-xs text-ink-muted">
          社外秘 / 株式会社マインド 社内利用限定
        </div>
      </footer>
    </div>
  );
}
